/**
 * Ohio patient data constants
 */

export const PAYER_TYPES = [
  { value: 'ODM', label: 'ODM Fee-for-Service' },
  { value: 'DODD', label: 'DODD' },
  { value: 'ODA', label: 'ODA (Aging)' },
  { value: 'ManagedCare', label: 'Managed Care' },
  { value: 'MyCare', label: 'MyCare Ohio' },
];

export const MANAGED_CARE_PLANS = [
  { value: 'Aetna', label: 'Aetna Better Health' },
  { value: 'Anthem', label: 'Anthem' },
  { value: 'Amerihealth Caritas', label: 'Amerihealth Caritas' },
  { value: 'Buckeye', label: 'Buckeye Health Plan' },
  { value: 'CareSource', label: 'CareSource' },
  { value: 'Humana', label: 'Humana' },
  { value: 'Molina', label: 'Molina Healthcare' },
  { value: 'Paramount', label: 'Paramount Advantage' },
  { value: 'UHC', label: 'United Healthcare' },
];

// Sandata PayerProgram values (from Appendix G)
export const PAYER_PROGRAMS = [
  { value: 'SP', label: 'SP — State Plan' },
  { value: 'SPHH', label: 'SPHH — State Plan Home Health' },
  { value: 'OHC', label: 'OHC — Ohio Home Care Waiver' },
  { value: 'OHCPD', label: 'OHCPD — OHC Private Duty' },
  { value: 'PP', label: 'PP — PASSPORT' },
  { value: 'PPPD', label: 'PPPD — PASSPORT Private Duty' },
  { value: 'DD', label: 'DD — Developmental Disabilities' },
  { value: 'PDHPC', label: 'PDHPC — PD Home Personal Care' },
  { value: 'MyC', label: 'MyC — MyCare Ohio' },
  { value: 'MyCPD', label: 'MyCPD — MyCare Private Duty' },
];

// Common Ohio EVV Procedure Codes
// `programs` lists Ohio Medicaid programs (from PAYER_PROGRAMS above) the
// code is valid for. State Plan codes (G0156, G0299, etc.) and Waiver codes
// (T1019, S5125, etc.) are distinct per ODM rule 5160-12-05 / 5160-46-06 —
// see backend/config/ohio_codes.py for the canonical mapping.
// Empty programs[] = "any program allowed" (back-compat).
export const PROCEDURE_CODES = [
  { value: 'G0156', label: 'G0156 — Home Health Aide',           programs: ['SP', 'SPHH'] },
  { value: 'G0299', label: 'G0299 — HH Skilled Nursing RN',      programs: ['SP', 'SPHH'] },
  { value: 'G0300', label: 'G0300 — HH Skilled Nursing LPN',     programs: ['SP', 'SPHH'] },
  { value: 'G0151', label: 'G0151 — PT Services',                programs: ['SP', 'SPHH'] },
  { value: 'G0152', label: 'G0152 — OT Services',                programs: ['SP', 'SPHH'] },
  { value: 'G0153', label: 'G0153 — Speech Therapy',             programs: ['SP', 'SPHH'] },
  { value: 'G0493', label: 'G0493 — Skilled Nursing 60min',      programs: ['SP', 'SPHH'] },
  { value: 'G0494', label: 'G0494 — Skilled Nursing 30min',      programs: ['SP', 'SPHH'] },
  { value: 'T1000', label: 'T1000 — Private Duty Nursing',       programs: ['SP', 'OHCPD', 'PPPD', 'MyCPD'] },
  { value: 'T1001', label: 'T1001 — Nursing Assessment/Eval',    programs: ['SP', 'OHC', 'PP', 'MyC'] },
  { value: 'T1002', label: 'T1002 — Nursing Services RN',        programs: ['SP', 'SPHH', 'OHC', 'PP', 'MyC'] },
  { value: 'T1003', label: 'T1003 — Nursing Services LPN/LVN',   programs: ['SP', 'SPHH', 'OHC', 'PP', 'MyC'] },
  { value: 'T1019', label: 'T1019 — Personal Care Aide',         programs: ['OHC', 'PP', 'MyC', 'PDHPC'] },
  { value: 'T2025', label: 'T2025 — Waiver Services',            programs: ['PP', 'MyC', 'DD'] },
  { value: 'S5125', label: 'S5125 — Attendant Care',             programs: ['OHC', 'PP', 'MyC'] },
  { value: 'HMPCR', label: 'HMPCR — Homemaker/Personal Care Routine (DODD)', programs: ['DD'] },
  { value: 'HMPCC', label: 'HMPCC — Homemaker/Personal Care Complex (DODD)', programs: ['DD'] },
  { value: 'HPC',   label: 'HPC — Home Personal Care (DODD)',    programs: ['DD'] },
  { value: 'ECL',   label: 'ECL — Environmental Control',        programs: ['DD'] },
  { value: 'RR',    label: 'RR — Respite Care',                  programs: ['DD'] },
  { value: 'BSR',   label: 'BSR — Behavioral Support Rate Mod (DODD)',  programs: ['DD'] },
  { value: 'MAR',   label: 'MAR — Medical Assistance Rate Mod (DODD)',  programs: ['DD'] },
];

/**
 * Return PROCEDURE_CODES for a given program. P2 (May 2026 Rhinobill
 * parity): we no longer filter the list — all codes are returned so the
 * user can pick any code/program pair (matching Rhinobill's behavior
 * where ODM/MCO contracts occasionally use non-canonical pairings).
 *
 * Use `isUnusualCodeProgramPair(code, program)` to render a soft warning
 * badge next to unusual selections.
 */
export const codesForProgram = (_program) => PROCEDURE_CODES;

/**
 * Return true if (code, program) is NOT in the canonical mapping —
 * i.e., this is an unusual pair worth surfacing as a soft warning.
 * Returns false (no warning) for falsy inputs or unknown codes
 * (back-compat: unknown custom codes are treated as universally valid).
 */
export const isUnusualCodeProgramPair = (code, program) => {
  if (!code || !program) return false;
  const codeEntry = PROCEDURE_CODES.find((c) => c.value === code);
  if (!codeEntry || !codeEntry.programs?.length) return false;
  return !codeEntry.programs.includes(program);
};

// Place of Service codes for EDI 837P
export const PLACE_OF_SERVICE = [
  { value: '12', label: '12 — Home' },
  { value: '11', label: '11 — Office' },
  { value: '31', label: '31 — Skilled Nursing Facility' },
  { value: '32', label: '32 — Nursing Facility' },
  { value: '34', label: '34 — Hospice' },
  { value: '99', label: '99 — Other' },
];

// Relationship to Subscriber for EDI 837P
export const RELATIONSHIP_CODES = [
  { value: '18', label: '18 — Self' },
  { value: '01', label: '01 — Spouse' },
  { value: '19', label: '19 — Child' },
  { value: '20', label: '20 — Employee' },
  { value: '21', label: '21 — Unknown' },
  { value: '39', label: '39 — Organ Donor' },
  { value: '40', label: '40 — Cadaver Donor' },
  { value: '53', label: '53 — Life Partner' },
  { value: 'G8', label: 'G8 — Other Relationship' },
];

export const SECONDARY_PAYER_TYPES = [
  { value: 'Medicare', label: 'Medicare' },
  { value: 'ODM', label: 'ODM Fee-for-Service' },
  { value: 'ManagedCare', label: 'Managed Care' },
  { value: 'Private', label: 'Private Insurance' },
  { value: 'None', label: 'None' },
];

// All 88 Ohio Counties
export const OHIO_COUNTIES = [
  'Adams', 'Allen', 'Ashland', 'Ashtabula', 'Athens',
  'Auglaize', 'Belmont', 'Brown', 'Butler', 'Carroll',
  'Champaign', 'Clark', 'Clermont', 'Clinton', 'Columbiana',
  'Coshocton', 'Crawford', 'Cuyahoga', 'Darke', 'Defiance',
  'Delaware', 'Erie', 'Fairfield', 'Fayette', 'Franklin',
  'Fulton', 'Gallia', 'Geauga', 'Greene', 'Guernsey',
  'Hamilton', 'Hancock', 'Hardin', 'Harrison', 'Henry',
  'Highland', 'Hocking', 'Holmes', 'Huron', 'Jackson',
  'Jefferson', 'Knox', 'Lake', 'Lawrence', 'Licking',
  'Logan', 'Lorain', 'Lucas', 'Madison', 'Mahoning',
  'Marion', 'Medina', 'Meigs', 'Mercer', 'Miami',
  'Monroe', 'Montgomery', 'Morgan', 'Morrow', 'Muskingum',
  'Noble', 'Ottawa', 'Paulding', 'Perry', 'Pickaway',
  'Pike', 'Portage', 'Preble', 'Putnam', 'Richland',
  'Ross', 'Sandusky', 'Scioto', 'Seneca', 'Shelby',
  'Stark', 'Summit', 'Trumbull', 'Tuscarawas', 'Union',
  'Van Wert', 'Vinton', 'Warren', 'Washington', 'Wayne',
  'Williams', 'Wood', 'Wyandot'
];

export const emptyPatient = {
  first_name: '',
  last_name: '',
  middle_name: '',
  suffix: '',
  date_of_birth: '',
  gender: '',
  medicaid_id: '',
  medicare_id: '',
  ssn: '',
  phone: '',
  address: '',
  address_line2: '',
  city: '',
  state: 'OH',
  zip_code: '',
  county: '',
  // Payer / Billing
  payer_type: '',
  payer_program: '',
  procedure_code: '',
  // Multi-row billing — each entry: { payer_program, procedure_code }.
  // The first entry mirrors the legacy fields above for backward compat.
  billing_lines: [{ payer_program: '', procedure_code: '' }],
  managed_care_plan: '',
  place_of_service: '12',
  relationship_to_subscriber: '18',
  is_newborn: false,
  payer_effective_start: '',
  payer_effective_end: '',
  // Physician/Referring Provider
  physician_npi: '',
  physician_name: '',
  physician_specialty: '',
  // Prior Authorization
  prior_auth_number: '',
  prior_auth_start_date: '',
  prior_auth_end_date: '',
  // Diagnosis Codes
  primary_icd10_code: '',
  primary_icd10_description: '',
  secondary_icd10_code: '',
  secondary_icd10_description: '',
  // Secondary Insurance
  secondary_payer_type: '',
  secondary_insurance_id: '',
  secondary_insurance_name: '',
  secondary_group_number: '',
  // Emergency Contact
  emergency_contact_name: '',
  emergency_contact_phone: '',
  notes: '',
};
